import { createSelector } from 'reselect';
import { isEmpty } from 'lodash';
import DomainFilterService from 'domain/FilterService';
import {
  AGENCY_ID,
  CHARGE_NAME,
  COMPANY_NAME,
  REGIST_DATE,
  UPDATE_DATE,
  USER_ID,
  USER_TYPE,
  MFA_ENABLED,
} from 'domain/fields';
import { UNREGISTERED_ID_FILTER_KEY } from 'domain/consts';
import { masterDataRecordsToSelectItems } from 'domain/utils';
import agencySelectors from 'store/agency-management/selectors';
import masterDataSelectors from 'store/master-data/selectors';
import {
  ROLE_CAPI_SETTING,
  ROLE_EBIS_API,
  ROLE_MANAGE_ACCOUNT,
  ROLE_MMM_ANALYSIS,
  ROLE_SETTING,
  ROLE_VIEW_AD,
  ROLE_VIEW_ALL,
} from 'domain/permissions/roles';
import { EDIT } from 'domain/user-management/consts';

const getList = (state) => {
  const { byIds } = state.cache.userManagement;
  return byIds;
};
const getById = (state, viewId) => {
  const { data } = state;
  if (!Object.prototype.hasOwnProperty.call(data, viewId)) {
    throw new Error(`Does not found view ${viewId}`);
  }
  const { id, view_name: name, status: selected } = data[viewId];
  return { id, name, selected };
};

const getStatusList = (state) => {
  const { status } = state.cache.userManagement.apiStatus.list;
  return status;
};

const getSelectedUser = (state) => {
  const { selected } = state.cache.userManagement;
  return selected;
};

const isShowModal = (state) => {
  const { isShowSettingModal } = state.cache.userManagement.settings;
  return isShowSettingModal;
};

const getApiError = createSelector(
  [agencySelectors.getErrorDetail, (state) => state.cache.userManagement.error],
  (agencyDetailErrors, userApiErrors) => {
    return isEmpty(agencyDetailErrors)
      ? userApiErrors?.data?.errors || []
      : agencyDetailErrors;
  }
);

const getCurrentUserNumber = (state) =>
  state.cache.userManagement.settings.pagination.totalItems;

const getSettings = (state) => state.cache.userManagement.settings;

const getCurrentPage = (state) =>
  state.cache.userManagement.settings.pagination.currentPage;

const getSort = (state) => state.cache.userManagement.settings.sort;

const getListAgency = createSelector(
  [
    masterDataSelectors.getStatus,
    masterDataSelectors.getAgency,
    masterDataSelectors.getSearchResultType(AGENCY_ID),
  ],
  (status, agencies, searchResult) => {
    const agencySetting = masterDataRecordsToSelectItems(
      DomainFilterService.uiMapperData(agencies),
      [UNREGISTERED_ID_FILTER_KEY]
    );
    return {
      isLoading: status === 'loading',
      list: searchResult
        ? DomainFilterService.uiMapperData(searchResult)
        : agencySetting,
    };
  }
);

const getHeader = (
  hasContractApi,
  isCapiSingleContract,
  isCapiContract,
  isMmmContract
) => {
  const header = [
    {
      field: 'rowId',
      fieldResponse: USER_ID,
      name: '',
      type: 'checkbox',
      isFreeze: true,
      isEdit: true,
    },
    {
      field: 'user_id_view',
      name: 'ユーザーID',
      isFreeze: true,
      type: 'inlineTruncate',
      minWidth: '166px',
    },
    {
      field: COMPANY_NAME,
      name: '会社名',
      isFreeze: true,
      type: 'inlineTruncate',
      minWidth: '166px',
    },
    {
      field: CHARGE_NAME,
      name: '名前',
      isFreeze: true,
      type: 'inlineTruncate',
      minWidth: '189px',
      sort: 'none',
    },
    {
      field: MFA_ENABLED,
      name: '多要素認証\n（MFA）',
      isFreeze: true,
      minWidth: '115px',
      needFormat: true,
    },
    {
      field: USER_TYPE,
      name: 'ユーザー種別',
      minWidth: '166px',
    },
    {
      field: 'function_permission',
      name: '機能権限',
      children: [
        ...(!isCapiSingleContract
          ? [
              {
                field: EDIT,
                name: '閲覧',
                children: [
                  {
                    field: ROLE_VIEW_ALL,
                    name: '全トラフィック',
                    needFormat: true,
                    sort: 'none',
                    variant: 'icon-check',
                    width: '107px',
                  },
                  {
                    field: ROLE_VIEW_AD,
                    name: '広告',
                    needFormat: true,
                    sort: 'none',
                    variant: 'icon-check',
                    width: '53px',
                  },
                ],
              },
            ]
          : []),
        {
          field: ROLE_SETTING,
          name: '編集',
          type: 'collapseByHeight',
          needFormat: true,
          sort: 'none',
          variant: 'icon-check',
          width: '53px',
        },
        ...(hasContractApi
          ? [
              {
                field: ROLE_EBIS_API,
                name: 'API',
                type: 'collapseByHeight',
                needFormat: true,
                sort: 'none',
                variant: 'icon-check',
                width: '53px',
              },
            ]
          : []),
        ...(isCapiContract
          ? [
              {
                field: ROLE_CAPI_SETTING,
                name: 'CAPiCO\n設定',
                type: 'collapseByHeight',
                needFormat: true,
                sort: 'none',
                variant: 'icon-check',
                width: '85px',
                textAlign: 'center',
              },
            ]
          : []),
        ...(isMmmContract
          ? [
              {
                field: ROLE_MMM_ANALYSIS,
                name: 'MMM\n分析',
                type: 'collapseByHeight',
                needFormat: true,
                sort: 'none',
                variant: 'icon-check',
                width: '85px',
                textAlign: 'center',
              },
            ]
          : []),
        {
          field: ROLE_MANAGE_ACCOUNT,
          name: 'アカウント\n管理',
          type: 'collapseByHeight',
          needFormat: true,
          sort: 'none',
          variant: 'icon-check',
          width: '85px',
          textAlign: 'center',
        },
      ],
    },
    {
      field: REGIST_DATE,
      name: '登録日時',
      needFormat: true,
      type: 'inlineNowrap',
    },
    {
      field: UPDATE_DATE,
      name: '最終更新日時',
      needFormat: true,
      type: 'inlineNowrap',
    },
  ];
  return header;
};

export default {
  getList,
  getById,
  getCurrentUserNumber,
  getStatusList,
  getSelectedUser,
  isShowModal,
  getApiError,
  getListAgency,
  getSettings,
  getCurrentPage,
  getHeader,
  getSort,
};
